import React, { useEffect, useState } from 'react';

import { Button, Modal } from '~components';
import Icon, { faDownload, faRefresh } from '~components/icon';
import NotificationButton from '~components/NotificationButton';
import { TableView } from '~components/table-view';

import { columns } from '~main/forms/document-batches/list';

import { useLazyGetDocumentBatchesQuery } from '~main/actions/documentBatches';

const DEBOUNCE_MS = 1000;

const DocumentBatchesMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isRefreshDisabled, setIsRefreshDisabled] = useState<boolean>(false);
  const [readyCount, setReadyCount] = useState<number>(0);

  const [getDocumentBatches, { data = [], isLoading }] = useLazyGetDocumentBatchesQuery();

  // Calculate count of completed batches ready for download. A batch whose jobs all failed still
  // rolls up to 'completed', so it needs at least one completed job to have produced anything -
  // without that check the badge announces a download for a row that offers none.
  useEffect(() => {
    const count = data.filter(
      (batch) => batch.status?.tag === 'completed' && batch.completed_jobs > 0 && !batch.downloaded_at,
    ).length;
    setReadyCount(count);
  }, [data]);

  // Fetch on mount
  useEffect(() => {
    getDocumentBatches();
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    getDocumentBatches();
  };

  const handleRefresh = () => {
    getDocumentBatches();
    setIsRefreshDisabled(true);
    setTimeout(() => {
      setIsRefreshDisabled(false);
    }, DEBOUNCE_MS);
  };

  return (
    <div>
      <NotificationButton count={readyCount} onClick={handleOpen}>
        <Icon icon={faDownload} />
      </NotificationButton>

      <Modal
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        bodyClassName="m-0 p-0"
        className="features"
        header="Document Batch Jobs"
        headerClassName="header"
        includeFooter={false}
        size="xl"
        onClosed={() => {
          setIsOpen(false);
        }}
      >
        <div className="modal-body">
          <div className="d-flex justify-content-end mb-3">
            <Button size="sm" color="success" onClick={handleRefresh} disabled={isLoading || isRefreshDisabled}>
              <Icon icon={faRefresh} className="me-1" />
              Refresh
            </Button>
          </div>
          <TableView
            id="document-batches-table"
            columns={columns}
            data={data}
            isLoading={isLoading}
            emptyMessage="No document batch jobs found"
            readonly
            striped
            bordered
          />
        </div>
      </Modal>
    </div>
  );
};

export default DocumentBatchesMenu;
